import * as React from "react";
import styled from "react-emotion";
import {Control, Field, Label, Select} from "bloomer";
import Card from "../../../elements/Card";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import Button from "../../../elements/Button";
import {getCrossProviderJobResult, startCrossProviderJob} from "../../../api/models/CrossProvider";
import Spacer from "../../../elements/Spacer";
import Config from "./Config";
import ProgressStep from "../../../components/model/CrossProvider/ProgressStep";
import ErrorUtils from "../../../utils/ErrorUtils";
import ErrorMessage from "../../../elements/ErrorMessage";
import {toast} from "react-toastify";
import ModelContentContainer from "../../../elements/ModelContentContainer";

const ModelContainer = styled("div")`
  margin-left: 20px;
`;

interface ICrossProviderModelState {
  loading: boolean;
  progressSteps: any;
  currentStep: number;
  firstModelId: string;
  firstModel: any;
  firstModelElement: any;
  firstModelElementRef: any;
  secondModelId: string;
  secondModel: any;
  secondModelElement: any;
  secondModelElementRef: any;
  secondLayerModelOptions: any;
  thirdModelId: string;
  thirdModel: any;
  thirdModelElement: any;
  thirdModelElementRef: any;
  thirdLayerModelOptions: any;
  result: any;
  resultModelElement: any;
  resultModelElementRef: any;
  jobErrorMessage: string;
}

class CrossProviderModel extends React.Component<{}, ICrossProviderModelState> {
  public JOB_STATUS_CHECK_INTERVAL: number = 3000; // 3 seconds
  public JOB_STATUS_CHECK_MAX_COUNT_PER_MODEL: number = 60;
  public timeout:any = null;
  public modelSelectStyle = {width: '300px'};

  public firstLayerModelOptions:any = [{
    id: "",
    name: "Select..."
  }, {
    id: "speech_recognition",
    name: "Speech Recognition"
  }, {
    id: "text_extraction",
    name: "Text Extraction"
  }, {
    id: "translation",
    name: "Translation"
  }, {
    id: "video_to_text",
    name: "Video To Text"
  }];

  public constructor(props: {}) {
    super(props);

    this.state = {
      loading: false,
      progressSteps: [],
      currentStep: 0,
      firstModelId: "",
      firstModel: null,
      firstModelElement: null,
      firstModelElementRef: null,
      secondModelId: "",
      secondModel: null,
      secondModelElement: null,
      secondModelElementRef: null,
      secondLayerModelOptions: [],
      thirdModelId: "",
      thirdModel: null,
      thirdModelElement: null,
      thirdModelElementRef: null,
      thirdLayerModelOptions: [],
      result: null,
      resultModelElement: null,
      resultModelElementRef: null,
      jobErrorMessage: ""
    };
  }

  public componentDidMount() {
    SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.CROSS_PROVIDER);
  }

  public componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  public getModelOptions(combinableModels: any) {
    const emptyItemArr = [{
      id: "",
      name: "Select..."
    }];
    return emptyItemArr.concat(combinableModels.sort((a: string, b: string) => {
      return a > b ? 1 : -1;
    }).map((modelUniqueId: string) => {
      const c = Config[modelUniqueId];
      if (c) {
        return {
          id: modelUniqueId,
          name: c.modelName
        };
      }
      return null;
    }).filter((item: any) => item));
  }

  public handleFirstLayerModelSelection = (event: React.FormEvent<HTMLSelectElement>) => {
    const modelId = event.currentTarget.value;
    const config = Config[modelId];
    if (config) {
      const elementRef = React.createRef();
      const element = React.createElement(config.inputForm, {
        ref: elementRef
      });

      let secondLayerModelOptions: any = [];
      if (config.combinableModels) {
          secondLayerModelOptions = this.getModelOptions(config.combinableModels);
      }
      this.setState({
        firstModelId: modelId,
        firstModel: config,
        firstModelElement: element,
        firstModelElementRef: elementRef,
        secondModelId: "",
        secondModel: null,
        secondModelElement: null,
        secondModelElementRef: null,
        secondLayerModelOptions,
        thirdModelId: "",
        thirdModel: null,
        thirdModelElement: null,
        thirdModelElementRef: null,
        thirdLayerModelOptions: [],
        result: null,
        resultModelElement: null,
        resultModelElementRef: null,
        jobErrorMessage: ""
      });
    } else {
      this.setState({
        firstModel: null,
        firstModelId: modelId,
        firstModelElement: null,
        firstModelElementRef: null,
        secondModelId: "",
        secondModel: null,
        secondModelElement: null,
        secondModelElementRef: null,
        secondLayerModelOptions: [],
        thirdModelId: "",
        thirdModel: null,
        thirdModelElement: null,
        thirdModelElementRef: null,
        thirdLayerModelOptions: [],
        result: null,
        resultModelElement: null,
        resultModelElementRef: null,
        jobErrorMessage: ""
      });
    }
  };

  public handleSecondLayerModelSelection = (event: React.FormEvent<HTMLSelectElement>) => {
    const modelId = event.currentTarget.value;
    const config = Config[modelId];
    if (config) {
      let elementRef = null;
      let element = null;
      if (config.hasMandatoryUserInputs) {
        elementRef = React.createRef();
        element = React.createElement(config.inputForm, {
          ref: elementRef,
          showUserInputsOnly: true
        });
      }

      const resultElementRef = React.createRef();
      const resultElement = React.createElement(config.outputForm, {
        ref: resultElementRef
      });

      let thirdLayerModelOptions: any = [];
      if (config.combinableModels) {
        thirdLayerModelOptions = this.getModelOptions(config.combinableModels);
      }

      this.setState({
        secondModelId: modelId,
        secondModel: config,
        secondModelElement: element,
        secondModelElementRef: elementRef,
        resultModelElement: resultElement,
        resultModelElementRef: resultElementRef,
        thirdModelId: "",
        thirdModel: null,
        thirdModelElement: null,
        thirdModelElementRef: null,
        thirdLayerModelOptions,
        result: null,
        jobErrorMessage: ""
      });
    } else {
      this.setState({
        secondModelId: modelId,
        secondModel: null,
        secondModelElement: null,
        secondModelElementRef: null,
        thirdModelId: "",
        thirdModel: null,
        thirdModelElement: null,
        thirdModelElementRef: null,
        thirdLayerModelOptions: [],
        resultModelElement: null,
        resultModelElementRef: null,
        result: null,
        jobErrorMessage: ""
      });
    }
  };

  public handleThirdLayerModelSelection = (event: React.FormEvent<HTMLSelectElement>) => {
    const modelId = event.currentTarget.value;
    const config = Config[modelId];
    if (config) {
      let elementRef = null;
      let element = null;
      if (config.hasMandatoryUserInputs) {
        elementRef = React.createRef();
        element = React.createElement(config.inputForm, {
          ref: elementRef,
          showUserInputsOnly: true
        });
      }

      const resultElementRef = React.createRef();
      const resultElement = React.createElement(config.outputForm, {
        ref: resultElementRef,
        result: this.state.result
      });

      this.setState({
        thirdModelId: modelId,
        thirdModel: config,
        thirdModelElement: element,
        thirdModelElementRef: elementRef,
        resultModelElement: resultElement,
        resultModelElementRef: resultElementRef,
        result: null,
        jobErrorMessage: ""
      });
    } else {
      let resultElement = null;
      const resultElementRef = React.createRef();
      const secondModel = this.state.secondModel;
      if (secondModel && secondModel.hasMandatoryUserInputs) {
        resultElement = React.createElement(secondModel.outputForm, {
          ref: resultElementRef
        });
      }
      this.setState({
        thirdModelId: modelId,
        thirdModel: null,
        thirdModelElement: null,
        thirdModelElementRef: null,
        resultModelElement: resultElement,
        resultModelElementRef: resultElementRef,
        result: null,
        jobErrorMessage: ""
      });
    }
  };

  public cancelChecking = (errorMessage: string) => {
    clearTimeout(this.timeout);
    this.setState({
      loading: false,
      result: "",
      jobErrorMessage: errorMessage
    });
  };

  public checkJobStatus = async (jobId: string, count: number, responseType: string) => {
    const modelsCount = this.state.thirdModel ? 3 : 2;
    if (count > this.JOB_STATUS_CHECK_MAX_COUNT_PER_MODEL * modelsCount) {
      this.cancelChecking("Timeout error.");
      return;
    }

    let errorOccurred = false;

    const response = await getCrossProviderJobResult(jobId, responseType).catch((error) => {
      if (ErrorUtils.requestLimitExceeded(error.response)) {
        this.cancelChecking(ErrorUtils.message.REQUEST_LIMIT_EXCEEDED);
        toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
      } else if (error.response && error.response.status === ErrorUtils.errorCode.THIRD_PARTY_SUPPLIER_ERROR) {
        this.cancelChecking(ErrorUtils.message.THIRD_PARTY_SUPPLIER_ERROR);
      } else {
        this.cancelChecking("Error Occurred.");
      }
      errorOccurred = true;
    });

    if (errorOccurred) {
      return;
    }

    if (response) {
      if (response.status === 210) {
        const responseData = response.data;
        if (responseData && responseData.metadata) {
          const step = responseData.metadata.step - 1;
          this.setState({
            currentStep: step
          });
        }
        this.timeout = setTimeout(() => {
          this.checkJobStatus(jobId, count + 1, responseType);
        }, this.JOB_STATUS_CHECK_INTERVAL);
      } else {
        this.setState({
          loading: false,
          result: response.data
        }, () => {
          const resultModelElementRef = this.state.resultModelElementRef;
          if (resultModelElementRef) {

            const lastModel = this.state.thirdModel ? this.state.thirdModel : this.state.secondModel;

            if (lastModel && lastModel.usesModelInput) {
              resultModelElementRef.current.processResponse(this.state.result, true);
            } else {
              resultModelElementRef.current.processResponse(this.state.result);
            }

            if (lastModel && lastModel.hasMandatoryUserInputs && lastModel.hasMetaData) {
              const lastModelElementRef = this.state.thirdModel ? this.state.thirdModelElementRef : this.state.secondModelElementRef;
              if (lastModelElementRef) {
                resultModelElementRef.current.setMetaData(lastModelElementRef.current.getMetaData());
              }
            }
          }
        });

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.CROSS_PROVIDER);
      }
    } else {
      this.cancelChecking("Error occurred.");
    }
  };

  public startJob = async (data: any, responseType: string, progressSteps: any) => {
    this.setState({
      loading: true,
      progressSteps,
      currentStep: 0,
      result: null,
      jobErrorMessage: ""
    });

    let errorOccurred = false;

    const response = await startCrossProviderJob(data).catch((error) => {
      if (ErrorUtils.requestLimitExceeded(error.response)) {
        this.setState({
          loading: false
        });
        toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
      } else {
        this.setState({
          loading: false,
          jobErrorMessage: "Couldn't start process."
        });
      }
      errorOccurred = true;
    });

    if (errorOccurred) {
      return;
    }

    if (response && response.data && response.data.job_id) {
      this.checkJobStatus(response.data.job_id, 1, responseType);
    } else {
      this.setState({
        loading: false,
        jobErrorMessage: "Couldn't start process."
      });
    }
  };

  public onSubmit = (e: any) => {
    e.target.blur();

    if (this.state.loading) {
      return;
    }

    const firstModel = this.state.firstModel;
    const firstModelElementRef = this.state.firstModelElementRef;
    const secondModel = this.state.secondModel;
    const secondModelElementRef = this.state.secondModelElementRef;
    if (!firstModel || !firstModelElementRef || !secondModel) {
      return;
    }

    if (secondModel.hasMandatoryUserInputs) {
      if (!secondModelElementRef) {
        return;
      }
    }

    const thirdModel = this.state.thirdModel;
    const thirdModelElementRef = this.state.thirdModelElementRef;
    if (thirdModel && thirdModel.hasMandatoryUserInputs) {
      if (!thirdModelElementRef) {
        return;
      }
    }

    const form = firstModelElementRef.current;
    if (!form) {
      return;
    }

    let valid = true;

    if (!form.validateForm()) {
      valid = false;
    }

    let form2 = null;
    if (secondModel.hasMandatoryUserInputs) {
      form2 = secondModelElementRef.current;

      if (!form2.validateForm()) {
        valid = false;
      }
    }

    let form3 = null;
    if (thirdModel && thirdModel.hasMandatoryUserInputs) {
      form3 = thirdModelElementRef.current;

      if (!form3.validateForm()) {
        valid = false;
      }
    }

    if (!valid) {
      return;
    }

    const data = form.getFormData(firstModel.modelId);
    if (secondModel.hasMandatoryUserInputs) {
      const form2Data = form2.getFormData(secondModel.modelId);
      form2Data.forEach((item: any, key: string) => {
        data.append(key, item);
      });
    }

    if (thirdModel && thirdModel.hasMandatoryUserInputs) {
      const form3Data = form3.getFormData(thirdModel.modelId);
      form3Data.forEach((item: any, key: string) => {
        data.append(key, item);
      });
    }

    const progressSteps = [firstModel.modelName, secondModel.modelName];

    const pipelineConfiguration: any = [{
      model_name: firstModel.modelId,
      args: firstModel.inputArguments.map((arg: any) => {
        arg.source = 'request';
        return arg;
      })
    }, {
      model_name: secondModel.modelId,
      args: secondModel.inputArguments.map((arg: any) => {
        if (arg.source === 'previous_model_output') {
          arg.output_field_name = firstModel.output.name;
          arg.type = "text";
        }
        return arg;
      })
    }];

    if (thirdModel) {
      progressSteps.push(thirdModel.modelName);
      pipelineConfiguration.push({
        model_name: thirdModel.modelId,
        args: thirdModel.inputArguments.map((arg: any) => {
          if (arg.source === 'previous_model_output') {
            arg.output_field_name = secondModel.output.name;
            arg.type = "text";
          }
          return arg;
        })
      });
    }

    const lastItem = pipelineConfiguration[pipelineConfiguration.length - 1];
    const lastModel = thirdModel ? thirdModel : secondModel;

    lastItem.send_output_as_file = lastModel.outputsFile;
    lastItem.output_file_mime_type = lastModel.outputFileMimeType;
    lastItem.output_field_name = lastModel.outputFieldName;
    lastItem.send_input_in_result = lastModel.usesModelInput;

    try {
      data.append('pipeline_configuration', JSON.stringify(pipelineConfiguration));
    } catch(e) {
      this.setState({
        jobErrorMessage: "Error occurred."
      });
      return;
    }

    this.startJob(data, lastModel.outputsFile ? 'blob' : undefined, progressSteps);
  };

  public onReset = (e: any) => {
    e.target.blur();

    if (this.state.loading) {
      return;
    }

    this.setState({
      loading: false,
      progressSteps: [],
      currentStep: 0,
      firstModelId: "",
      firstModel: null,
      firstModelElement: null,
      firstModelElementRef: null,
      secondModelId: "",
      secondModel: null,
      secondModelElement: null,
      secondModelElementRef: null,
      secondLayerModelOptions: [],
      thirdModelId: "",
      thirdModel: null,
      thirdModelElement: null,
      thirdModelElementRef: null,
      thirdLayerModelOptions: [],
      result: null,
      resultModelElement: null,
      resultModelElementRef: null,
      jobErrorMessage: ""
    });

    clearTimeout(this.timeout);
  };

  public render() {
    return (
      <div style={{width: '90%'}}>
        <ModelContentContainer>
          <Card>
            <Field>
              <Label>Select Model to Start With:</Label>
              <Control>
                <Select
                    disabled={this.state.loading}
                    style={this.modelSelectStyle}
                    value={this.state.firstModelId}
                    onChange={this.handleFirstLayerModelSelection}
                >
                  {
                    this.firstLayerModelOptions.map((model: any) => {
                      return <option key={model.id} value={model.id}>{model.name}</option>;
                    })
                  }
                </Select>
              </Control>
            </Field>
            {
              this.state.firstModel ? <ModelContainer>{this.state.firstModelElement}</ModelContainer> : <></>
            }
            <Spacer/>
            {
              this.state.firstModel ? <Field>
                <Label>
                {
                  this.state.secondModel && this.state.thirdLayerModelOptions.length
                      ? "Select Model to Continue With:" : "Select Model to Process Output:"
                }
                </Label>
                <Control>
                  <Select
                      disabled={this.state.loading}
                      style={this.modelSelectStyle}
                      value={this.state.secondModelId}
                      onChange={this.handleSecondLayerModelSelection}
                  >
                    {
                      this.state.secondLayerModelOptions.map((model: any) => {
                        return <option key={model.id} value={model.id}>{model.name}</option>;
                      })
                    }
                  </Select>
                </Control>
              </Field> : <></>
            }
            {
              this.state.secondModel && this.state.secondModel.hasMandatoryUserInputs ?
                  <ModelContainer>{this.state.secondModelElement}</ModelContainer> : <></>
            }
            <Spacer/>
            {
              this.state.secondModel && this.state.thirdLayerModelOptions.length ? <Field>
                <Label>Select Model to Process Output:</Label>
                <Control>
                  <Select
                      disabled={this.state.loading}
                      style={this.modelSelectStyle}
                      value={this.state.thirdModelId}
                      onChange={this.handleThirdLayerModelSelection}
                  >
                    {
                      this.state.thirdLayerModelOptions.map((model: any) => {
                        return <option key={model.id} value={model.id}>{model.name}</option>;
                      })
                    }
                  </Select>
                </Control>
              </Field> : <></>
            }
            {
              this.state.thirdModel && this.state.thirdModel.hasMandatoryUserInputs ?
                  <ModelContainer>{this.state.thirdModelElement}</ModelContainer> : <></>
            }
            <Spacer/>
            {
              this.state.resultModelElement ? <div>
                <Button onClick={this.onSubmit}>Submit</Button>
                <Button onClick={this.onReset} style={{marginLeft: "5px"}}>Reset</Button>
              </div> : <></>
            }
            <Spacer/>
            {
              this.state.loading || this.state.jobErrorMessage ? <div>
                {
                  this.state.progressSteps.map((text: string, index: number) => {
                    const currentStep = this.state.currentStep;
                    let inProgress = currentStep === index;
                    const finished = currentStep > index;
                    let failed = false;
                    let errorMessage = '';
                    if (inProgress && this.state.jobErrorMessage) {
                      errorMessage = this.state.jobErrorMessage;
                      failed = true;
                      inProgress = false;
                    }
                    return <ProgressStep
                        key={index + "." + text}
                        text={text}
                        inProgress={inProgress}
                        finished={finished}
                        failed={failed}
                        errorMessage={errorMessage}
                    />;
                  })
                }
              </div> : <></>
            }
            {
              !this.state.loading && this.state.result ? <div>
                <Label>Output:</Label>
                <ModelContainer>{this.state.resultModelElement}</ModelContainer>
              </div> : <></>
            }
            {
              !this.state.loading && this.state.jobErrorMessage ? <ErrorMessage>{this.state.jobErrorMessage}</ErrorMessage> : <></>
            }
          </Card>
        </ModelContentContainer>
      </div>
    );
  }
}

export default CrossProviderModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/CrossProvider/CrossProviderModel.tsx